import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CBadge, CButton, CCard, CCardBody, CFormCheck, CFormInput } from "@coreui/react";
import { PaginationControls } from "@src/components/PaginationControls";
import { tdApi } from "@src/services/api/tdApi";
import type { ApiExecutionGroup } from "@tradingdroid/frontends-types";
import { CDataTable } from "@tradingdroid/frontends-components";
import type { CDataTableColumn } from "@tradingdroid/frontends-components";
import { formatDateTimeShort } from "@src/utils/dateUtils";
import { isExecutionActive } from "@src/utils/executionStatus";

const PAGE_SIZE = 20;

// A group is active if any member execution is still active. Counts suspended/recovering (not just
// literal "running") so an auto-suspended execution's group doesn't drop out of the active view.
const isGroupActive = (g: ApiExecutionGroup): boolean =>
  !!g.executions?.some((ex) => isExecutionActive(ex));

export const ExecGroupsListPage: React.FC = () => {
  const [allGroups, setAllGroups] = useState<ApiExecutionGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [activeOnly, setActiveOnly] = useState(true);
  const [nameFilter, setNameFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(PAGE_SIZE);

  useEffect(() => {
    tdApi
      .getExecutionGroups({ embed: "executions" })
      .then((result) => {
        setAllGroups(result?.items || []);
      })
      .catch((err) => {
        console.error("[ExecGroupsListPage] Failed to fetch groups:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredGroups = useMemo(() => {
    let groups = allGroups;

    groups = groups.filter((g) => isGroupActive(g) === activeOnly);

    if (nameFilter.trim()) {
      const lower = nameFilter.toLowerCase();
      groups = groups.filter((g) => g.name.toLowerCase().includes(lower));
    }

    return groups;
  }, [allGroups, activeOnly, nameFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [activeOnly, nameFilter]);

  const pageGroups = useMemo(() => {
    const start = page * perPage;
    return filteredGroups.slice(start, start + perPage);
  }, [filteredGroups, page, perPage]);

  const handlePerPageChange = useCallback((newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(0);
  }, []);

  const tableColumns = useMemo<CDataTableColumn<ApiExecutionGroup>[]>(
    () => [
      {
        columnKey: "id",
        columnLabel: "ID",
        columnStyle: { width: "70px" },
        cellRender: (g) => (
          <Link className="td-idLink" to={`/exec-group/${g.id}`}>
            {g.id}
          </Link>
        ),
      },
      {
        columnKey: "name",
        columnLabel: "Name",
        columnStyle: { width: "auto" },
        cellRender: (g) => g.name,
      },
      {
        columnKey: "executions",
        columnLabel: "Executions",
        columnStyle: { width: "100px", textAlign: "right" as const },
        cellRender: (g) => g.executions?.length ?? 0,
      },
      {
        columnKey: "status",
        columnLabel: "Status",
        columnStyle: { width: "90px", textAlign: "right" as const },
        cellRender: (g) => {
          const active = isGroupActive(g);
          return (
            <CBadge color={active ? "success" : "secondary"} size="sm">
              {active ? "active" : "stopped"}
            </CBadge>
          );
        },
      },
      {
        columnKey: "created",
        columnLabel: "Created",
        columnStyle: { width: "140px" },
        cellRender: (g) => (g.create_ts ? formatDateTimeShort(g.create_ts) : "-"),
      },
    ],
    [],
  );

  return (
    <CCard>
      <CCardBody>
        <div className="ExecGroupsFilters d-flex align-items-center gap-3 mb-3">
          <CFormCheck
            id="activeOnlyFilter"
            label="Active"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          <CFormInput
            className="ExecGroupsFilters-nameInput"
            placeholder="Filter by name..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            size="sm"
            style={{ maxWidth: "250px" }}
          />
          {(nameFilter || !activeOnly) && (
            <CButton
              color="secondary"
              variant="ghost"
              size="sm"
              onClick={() => {
                setActiveOnly(true);
                setNameFilter("");
              }}
            >
              Clear
            </CButton>
          )}
          <span className="text-muted small ms-2">
            {filteredGroups.length} group{filteredGroups.length !== 1 ? "s" : ""}
          </span>
        </div>

        <PaginationControls
          itemLabel="groups"
          currentPage={page}
          totalItems={filteredGroups.length}
          perPage={perPage}
          onPerPageChange={handlePerPageChange}
          onPrevPage={() => setPage((p) => Math.max(0, p - 1))}
          onNextPage={() => setPage((p) => p + 1)}
          isLoading={loading}
          hasMore={false}
          defaultPageSize={PAGE_SIZE}
        />

        <CDataTable
          className="ExecGroupsListTable"
          columns={tableColumns}
          items={pageGroups}
          striped
          small
          hover
          responsive
          loading={loading}
          getRowProps={(g) => ({ id: `group-${g.id}` })}
          getCellProps={(g, column) => ({ id: `group-${g.id}-${column.columnKey}` })}
        />
      </CCardBody>
    </CCard>
  );
};
